/* eslint-disable max-lines */

/* eslint-disable import/max-dependencies */
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { FC, useEffect, useState } from 'react';
import LabeledField from 'src/components/Form/LabeledField';
import SearchBarWithChipsInsideInput, {
  SearchOption,
} from './SearchBarWithChips';
import { useCustomerGroupSearchQuery } from 'src/views/customerGroups/hooks/customerGroupSearchQuery';
import useDebouncedState from 'src/hooks/useDebouncedState';
import { useGetProductGroups } from 'src/views/productGroups/hooks/useProductGroups';
import {
  useCreateIngramProgram,
  useUpdateIngramProgram,
} from '../hooks/ingramProgramsMutations';
import DialogWrapper from './DialogWrapper';
import { IngramProgramDetailResponse } from '../hooks/ingramProgramsSearch';
import useSnackBar from 'src/hooks/useSnackBar';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import {
  useIngramProgramForm,
  useIsIngramProgramFormUpdated,
} from '../hooks/ingramProgramForms';
import Button from '@mui/material/Button';
import { Plus } from '@im/ui-lib/zenith/icons';

interface ProgramFormProps {
  buttonName: string;
  programDetails?: IngramProgramDetailResponse;
}

const ProgramForm: FC<ProgramFormProps> = ({ buttonName, programDetails }) => {
  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();

  const [formData, setFormData] = useIngramProgramForm(programDetails);
  const [isFormUpdated, setIsFormUpdated] = useIsIngramProgramFormUpdated();
  const [isRequiredFilled, setIsRequiredFilled] = useState<boolean>(true);
  const [openProgramDialog, setOpenProgramDialog] = useState<boolean>(false);

  useEffect(() => {
    setIsRequiredFilled(
      !formData?.requestPreamble?.ingramProgramName ||
        !formData?.requestPreamble?.programsDescription ||
        !formData?.manualTemplatesFieldProperties?.vendorACOPDiscountType
    );
  }, [formData]);

  const handleFieldChange = (field: string, value: string | SearchOption[]) => {
    switch (field) {
      case 'customerGroup':
        if (typeof value !== 'string') {
          setFormData((prev) => ({
            ...prev,
            requestPreamble: {
              ...prev?.requestPreamble,
              customerGroupInfo: value.map((option) => ({
                customer_group_id: option.id,
                customergroupname: option.label,
              })),
            },
          }));
        }
        break;
      case 'productGroup':
        if (typeof value !== 'string') {
          setFormData((prev) => ({
            ...prev,
            requestPreamble: {
              ...prev?.requestPreamble,
              productGroupInfo: value.map((option) => ({
                productGroupId: option.id,
                productGroupName: option.label,
              })),
            },
          }));
        }
        break;
      case 'programType':
        if (typeof value === 'string')
          setFormData((prev) => ({
            ...prev,
            manualTemplatesFieldProperties: {
              vendorACOPDiscountType: value,
            },
          }));
        break;
      default:
        setFormData((prev) => ({
          ...prev,
          requestPreamble: { ...prev?.requestPreamble, [field]: value },
        }));
        break;
    }
  };

  const [productGroupName, setProductGroupName] = useState<string>('');
  const [customerGroupName, setCustomerGroupName] = useState<string>('');

  const { data: { list } = {} } = useCustomerGroupSearchQuery(
    useDebouncedState(
      {
        ...(Boolean(customerGroupName) && { name: customerGroupName }),
        page: 1,
        pageSize: 100,
      },
      300
    ),
    undefined,
    Boolean(customerGroupName)
  );
  const { data: { data } = {} } = useGetProductGroups(
    useDebouncedState(
      {
        ...(Boolean(productGroupName) && { name: productGroupName }),
        page: 1,
        pageSize: 100,
      },
      300
    ),
    Boolean(productGroupName)
  );

  const programTypeOptions = [
    'CompanyWideDiscount',
    'ResellerDiscount',
    'ResellerEndUserDiscount',
  ].map((o) => ({ label: o, value: o }));

  const createProgram = useCreateIngramProgram(formData);

  const updateProgram = useUpdateIngramProgram(formData);

  const handleOnClose = () => {
    setFormData(
      programDetails
        ? programDetails.ingramVendorProgramDetail
        : {
            manualTemplatesFieldProperties: {
              vendorACOPDiscountType: 'CompanyWideDiscount',
            },
          }
    );
    setIsFormUpdated(false);
    setOpenProgramDialog(false);
  };

  const handleOnSubmit = () => {
    (programDetails ? updateProgram : createProgram)
      .mutateAsync()
      .then(() => {
        handleOnClose();
        showSnackBar({
          autoHide: 2000,
          message: translate(
            programDetails
              ? 'IngramProgram.EditProgramSuccess'
              : 'IngramProgram.AddNewProgramSuccess'
          ),
          severity: 'success',
        });
      })
      .catch(() => {
        showSnackBar({
          autoHide: 2000,
          message: translate(
            programDetails
              ? 'IngramProgram.EditProgramError'
              : 'IngramProgram.AddNewProgramError'
          ),
          severity: 'error',
        });
      });
  };

  return (
    <>
      <Button
        onClick={() => setOpenProgramDialog(true)}
        startIcon={programDetails ? undefined : <Plus />}
        variant={programDetails ? 'newOutlined' : 'newContained'}
      >
        {buttonName}
      </Button>
      <DialogWrapper
        dialogTitle={translate(
          programDetails
            ? 'IngramProgram.EditProgram'
            : 'IngramProgram.AddNewProgram'
        )}
        disabled={isRequiredFilled}
        isFormUpdated={isFormUpdated}
        onClose={handleOnClose}
        onSubmit={handleOnSubmit}
        open={openProgramDialog}
      >
        <Box>
          {createProgram.isLoading ||
            (updateProgram.isLoading && <CenterLoader overlay={true} />)}
          <Stack gap={1}>
            <LabeledField isRequired label={translate('Common.Title')}>
              <TextField
                aria-describedby={translate('Common.Title')}
                helperText={`${
                  50 -
                  (formData.requestPreamble?.ingramProgramName?.length ?? 0)
                } characters left`}
                inputProps={{ maxLength: 50 }}
                margin="normal"
                name="ingramProgramName"
                onChange={(e) =>
                  handleFieldChange(e.target.name, e.target.value)
                }
                placeholder={translate('Common.TitlePlaceholder')}
                sx={{
                  margin: '0px',
                }}
                value={formData?.requestPreamble?.ingramProgramName ?? ''}
              />
            </LabeledField>
            <LabeledField isRequired label={translate('Common.Description')}>
              <TextField
                aria-describedby={translate('Common.Description')}
                helperText={`${
                  300 -
                  (formData.requestPreamble?.programsDescription?.length ?? 0)
                } characters left`}
                inputProps={{ maxLength: 300 }}
                margin="normal"
                maxRows={10}
                minRows={5}
                multiline
                name="programsDescription"
                onChange={(e) =>
                  handleFieldChange(e.target.name, e.target.value)
                }
                placeholder={translate('Common.DescriptionPlaceholder')}
                sx={{
                  margin: '0px',
                }}
                value={formData?.requestPreamble?.programsDescription ?? ''}
              />
            </LabeledField>
            <LabeledField
              isRequired
              label={translate('IngramProgram.ProgramType')}
            >
              <Select
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                    },
                  },
                }}
                displayEmpty
                fullWidth
                input={<OutlinedInput />}
                name="programTypeDropdown"
                onChange={(e) =>
                  handleFieldChange('programType', e.target.value)
                }
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <em style={{ color: '#8B8B8B' }}>
                        {translate('IngramProgram.ProgramTypePlacholder')}
                      </em>
                    );
                  }

                  return selected;
                }}
                sx={{ fontSize: '14px', height: '34px' }}
                value={
                  formData?.manualTemplatesFieldProperties
                    ?.vendorACOPDiscountType ?? ''
                }
              >
                {programTypeOptions.map((option: any) => (
                  <MenuItem key={`${option.value}`} value={option.value}>
                    <Typography variant="bodycopyregular">
                      {option.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </LabeledField>
            <LabeledField label={translate('IngramProgram.ProductGroup')}>
              <SearchBarWithChipsInsideInput
                searchOptions={
                  data?.map(({ id, name }) => ({ id, label: name })) ?? []
                }
                selectedOptions={
                  formData?.requestPreamble?.productGroupInfo?.map(
                    ({ productGroupId, productGroupName }) => ({
                      id: productGroupId,
                      label: productGroupName,
                    })
                  ) ?? []
                }
                setGroupName={setProductGroupName}
                setSelectedOptions={(options) =>
                  handleFieldChange('productGroup', options)
                }
                testId="product-group"
              />
            </LabeledField>
            <LabeledField label={translate('IngramProgram.CustomerGroup')}>
              <SearchBarWithChipsInsideInput
                searchOptions={
                  list?.map(({ id, name }) => ({
                    id: Number(id),
                    label: name,
                  })) ?? []
                }
                selectedOptions={
                  formData?.requestPreamble?.customerGroupInfo?.map(
                    ({ customer_group_id, customergroupname }) => ({
                      id: customer_group_id,
                      label: customergroupname,
                    })
                  ) ?? []
                }
                setGroupName={setCustomerGroupName}
                setSelectedOptions={(options) =>
                  handleFieldChange('customerGroup', options)
                }
                testId="customer-group"
              />
            </LabeledField>
          </Stack>
        </Box>
      </DialogWrapper>
    </>
  );
};

export default ProgramForm;
